/** Provider API key validation — hits each provider's models endpoint to verify the key works. */

import { PROVIDER_ENDPOINTS, resolveProviderModelsUrl } from "./provider-endpoints.js";

/**
 * Result of an API key validation check performed by {@link validateApiKey}.
 *
 * @docLink packages/bridge/api-reference#validation
 */
export interface ValidationResult {
  /** `true` when the API key was accepted by the provider's models endpoint. */
  ok: boolean;
  /** Human-readable error description when `ok` is `false`. */
  error?: string;
}

/**
 * Validate an API key by sending a lightweight GET request to the provider's models endpoint.
 *
 * Returns `{ ok: true }` when the provider accepts the key (HTTP 2xx). Returns
 * `{ ok: false, error }` on 401/403 (invalid key), other HTTP errors, network failures,
 * or unsupported providers. Requests time out after 8 seconds.
 *
 * @param provider - Provider name matching a key in the shared `PROVIDER_ENDPOINTS` map
 *   (e.g. `"anthropic"`, `"openai"`, `"google"`).
 * @param key - API key to validate.
 * @param options - Optional base-URL / environment overrides; defaults to the
 *   provider's own API, or its `baseUrlEnv` value when one is set.
 * @returns Validation result with optional error description.
 * @docLink packages/bridge/api-reference#validation
 */
export async function validateApiKey(
  provider: string,
  key: string,
  options: { baseUrl?: string; env?: Record<string, string | undefined> } = {},
): Promise<ValidationResult> {
  const endpoint = PROVIDER_ENDPOINTS[provider];
  if (!endpoint) return { ok: false, error: `Unknown provider: ${provider}` };

  // Validate against the same endpoint discovery/dispatch use: behind a gateway
  // the key is the gateway's, and the provider's public API would reject it.
  let url =
    resolveProviderModelsUrl(provider, {
      baseUrl: options.baseUrl,
      env: options.env ?? process.env,
    }) ?? endpoint.url;
  if (endpoint.queryParam) url += `?${endpoint.queryParam}=${encodeURIComponent(key)}`;

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    const response = await fetch(url, {
      method: "GET",
      headers: endpoint.headers(key),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (response.ok) return { ok: true };

    const status = response.status;
    if (status === 401 || status === 403) {
      return { ok: false, error: "Invalid API key (authentication failed)" };
    }
    return { ok: false, error: `API returned HTTP ${status}` };
  } catch (err: any) {
    if (err.name === "AbortError") return { ok: false, error: "Request timed out (8s)" };
    return { ok: false, error: err.message ?? "Network error" };
  }
}
